import React, { FC, useState, useEffect } from 'react'
import { AlertProps } from './Alert.types'
import { createAlertId } from './create-alert-id'
import { AlertMessageRecord } from './AlertMessageRecord.type'
import { getValueListBgColor } from '../../../utils/background-util'
import { sanitizeHTML } from '../../../utils/common-utils'
import { setConfigVertSpacing } from '../../../utils/layout-util'
import { BrandIcon } from '../../Compounds/src/BrandIconCompound'
import { Notification } from '../../Notification/src/Notification'
import { NotificationType } from '../../Notification/src/NotificationType.type'

const ALERT_STATUS_RETENTION_PERIOD_DAYS = 60
const ALERT_STORAGE_KEY = 'alert-messages'
const ALERT_CLOSED_STATUS = 'CLOSED'
const ALERT_NOTIFICATION_SHOW = 'SHOW'
const ALERT_NOTIFICATION_HIDE = 'HIDE'

export const Alert: FC<AlertProps> = props => {
  const { content, testId } = props
  const {
    icon,
    iconColor,
    body,
    bgColor,
    heading,
    linkHref,
    linkTarget,
    linkText,
    showCloseButton,
    analytics,
    size,
    identifier,
    layoutConfigs,
    notificationMessage,
  } = content || {}

  const verticalSpace = setConfigVertSpacing(layoutConfigs)

  function isString(value: any): value is string {
    return typeof value === 'string'
  }

  const [isDismissed, setIsDismissed] = useState(false)
  const [showNotification, setShowNotification] = useState(true)
  const messageId = createAlertId(`${heading} + ${body}`)

  const bgColorClass = isString(bgColor) && getValueListBgColor(bgColor)

  useEffect(() => {
    if (showCloseButton !== 'true') {
      return
    }
    const now = new Date()
    const storageRecord = localStorage.getItem(ALERT_STORAGE_KEY)

    if (storageRecord === null) {
      return
    }

    const dismissedMessages = JSON.parse(storageRecord)

    dismissedMessages.forEach((message: AlertMessageRecord) => {
      if (message.id === messageId) {
        const storedDate = new Date(message.date)
        const differenceInMs = now.valueOf() - storedDate.valueOf()
        const differenceInDays = differenceInMs / (1000 * 60 * 60 * 24)
        if (differenceInDays <= ALERT_STATUS_RETENTION_PERIOD_DAYS) {
          setIsDismissed(true)
          message.notification = ALERT_NOTIFICATION_HIDE
          setShowNotification(false)
        } else {
          message.notification = ALERT_NOTIFICATION_SHOW
        }
      }
    })
    localStorage.setItem(ALERT_STORAGE_KEY, JSON.stringify(dismissedMessages))
  }, [setIsDismissed, messageId, showCloseButton])

  const handleUndoClick: (id: string) => void = id => {
    setIsDismissed(false)
    const record = localStorage.getItem(ALERT_STORAGE_KEY)
    if (record === null) {
      return
    }
    const messages = JSON.parse(record)
    messages.forEach((message: AlertMessageRecord, index: number) => {
      if (message.id === id) {
        messages.splice(index, 1)
      }
    })
    localStorage.setItem(ALERT_STORAGE_KEY, JSON.stringify(messages))
  }

  const handleCloseNotification: (closed: boolean) => void = closed => {
    if (closed) {
      const records = localStorage.getItem(ALERT_STORAGE_KEY)
      if (records === null) {
        return
      }
      const messagesData = JSON.parse(records)
      messagesData.forEach((message: AlertMessageRecord) => {
        if (message.id === messageId) {
          message.notification = ALERT_NOTIFICATION_HIDE
        }
      })
      localStorage.setItem(ALERT_STORAGE_KEY, JSON.stringify(messagesData))
    }
  }

  function handleCloseButton() {
    setIsDismissed(true)
    const storageRecord = localStorage.getItem(ALERT_STORAGE_KEY)
    let dismissedMessages = []
    if (storageRecord !== null) {
      dismissedMessages = JSON.parse(storageRecord)
    }

    const dismissedDateTime = new Date()
    dismissedMessages.push({
      id: messageId,
      date: dismissedDateTime.toISOString(),
      status: ALERT_CLOSED_STATUS,
      notification: ALERT_NOTIFICATION_SHOW,
    })
    localStorage.setItem(ALERT_STORAGE_KEY, JSON.stringify(dismissedMessages))
  }

  if (isDismissed && !showNotification) {
    return null
  }

  if (isDismissed && showNotification) {
    return (
      <Notification
        analytics={analytics ? `${analytics}-notification` : undefined}
        type={NotificationType.CONFIRM}
        dismissable={true}
        handleCloseNotification={handleCloseNotification}
        message={notificationMessage}
        testId={testId ? `${testId}-notification` : undefined}>
        <button
          type='button'
          className='Link u-inlineBlock u-marginLeft2gu'
          data-tracking='Button'
          onClick={() => handleUndoClick(messageId)}
          data-analytics='notification-undo'
          data-testid={testId ? `${testId}-notification-undo` : null}>
          Undo
        </button>
      </Notification>
    )
  }

  return (
    <>
      {!isDismissed && (
        <div className={`${verticalSpace}`}>
          <section className={`Alert ${bgColorClass}`} data-testid={testId ? `${testId}` : null}>
            <div className='u-sizeConstrained u-paddingVert4gu u-sm-hidden u-md-hidden'>
              <div className='u-flex u-flexAlignItemsStart'>
                {icon && (
                  <BrandIcon
                    iconsDropdown={icon}
                    iconsColorsDropdown={'u-fillColorDarkMint'}
                    size={size}
                    identifier={identifier}
                    className={`Alert-icon u-marginRight5gu`}
                    testId={`${testId}-desktop-icon`}
                  />
                )}
                <div className='u-flex u-flexCol'>
                  <h3
                    className='u-textColorNeutral u-flexShrink0'
                    data-testid={testId ? `${testId}-heading` : null}>
                    {heading}
                  </h3>
                  {body && (
                    <div
                      className='Content u-textColorNeutral u-marginTop4gu'
                      dangerouslySetInnerHTML={{ __html: sanitizeHTML(body) }}
                      data-testid={testId ? `${testId}-body` : null}></div>
                  )}
                </div>
                <div className='u-flex u-flexJustifyCenter u-flexAlignItemsCenter u-flexExpandLeft u-flexShrink0'>
                  {linkHref && (
                    <a
                      className='Alert-button Button Button--small Button--secondary Button--secondary--action-blue u-textColorActionBlue u-marginHoriz5gu'
                      href={linkHref}
                      target={linkTarget}
                      data-analytics={analytics}
                      data-testid={testId ? `${testId}-link` : null}>
                      {linkText}
                    </a>
                  )}
                  {showCloseButton && (
                    <button
                      title='Close'
                      aria-label='Close'
                      className='adel-material-symbols Alert-closeIcon u-textColorNeutral'
                      data-testid={testId ? `${testId}-close-button` : null}
                      onClick={() => {
                        handleCloseButton()
                      }}>
                      close
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className='u-sizeConstrained u-paddingVert4gu u-lg-hidden'>
              <div className='u-flex u-flexCol u-alignItemsBaseline'>
                <div className='u-flex u-flexAlignItemsStretch'>
                  {icon && (
                    <BrandIcon
                      iconsDropdown={icon}
                      iconsColorsDropdown={'u-fillColorDarkMint'}
                      size={size}
                      identifier={identifier}
                      className={`Alert-icon u-marginRight4gu`}
                      testId={`${testId}-mobile-icon`}
                    />
                  )}
                  <h3 className='u-textColorNeutral u-marginRight4gu'>{heading}</h3>
                  {showCloseButton && (
                    <div className='u-flexExpandLeft'>
                      <button
                        title='Close'
                        aria-label='Close'
                        className='adel-material-symbols u-textColorNeutral'
                        data-testid={testId ? `${testId}-close-button` : null}
                        onClick={() => {
                          handleCloseButton()
                        }}>
                        close
                      </button>
                    </div>
                  )}
                </div>
                {body && (
                  <div
                    className='Content u-marginVert4gu u-textColorNeutral'
                    dangerouslySetInnerHTML={{ __html: sanitizeHTML(body) }}></div>
                )}
                {linkHref && (
                  <a
                    className='u-md-hidden Alert-button Button Button--secondary Button--secondary--action-blue u-textColorActionBlue u-sizeFull u-flexAlignItemsCenter u-flexJustifyCenter'
                    href={linkHref}
                    target={linkTarget}
                    data-analytics={analytics}>
                    {linkText}
                  </a>
                )}
              </div>
              <div className='u-flexExpandLeft u-sm-hidden u-sm-marginLeft0 u-sm-marginTop4gu u-flexShrink0'>
                {linkHref && (
                  <a
                    className='Alert-button Button Button--secondary Button--secondary--action-blue u-textColorActionBlue'
                    href={linkHref}
                    target={linkTarget}
                    data-analytics={analytics}>
                    {linkText}
                  </a>
                )}
              </div>
            </div>
          </section>
        </div>
      )}
    </>
  )
}
